import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Button,
  IconButton,
  InputBase,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Header from "../../components/Header";
import LoadingSpinner from "../../loadingSpinner";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import secureStorage from "../../utils/secureStorage";
import { getPedidosPendientes } from "../../services/pedidoVenta.services";
import ModalCamion from "../../modal/camiones/modalCamion";
import { postOrdenCarga } from "../../services/ordenCarga.services";

const TUTORIAL_STEPS = [
  {
    label: "1. Vehículo",
    elementId: "tut-vehiculo",
    description:
      "Selecciona el camión que realizará el viaje. Solo aparecen vehículos disponibles. La placa se usa al generar la orden.",
  },
  {
    label: "2. Motorista",
    elementId: "tut-motorista",
    description:
      "Ingresa el nombre del conductor asignado a este viaje. Es un campo obligatorio para registrar la orden.",
  },
  {
    label: "3. Fecha de Carga",
    elementId: "tut-fecha-carga",
    description:
      "Define la fecha en que se realizará la carga del camión. Por defecto es el día de hoy.",
  },
  {
    label: "4. Tara Kg",
    elementId: "tut-tara",
    description:
      "Peso en vacío del camión (tara). Se suma al peso bruto para calcular el peso total de la carga.",
  },
  {
    label: "5. Caballete Cant.",
    elementId: "tut-caballete",
    description:
      "Cantidad de caballetes que irán en el camión. Cada caballete pesa aproximadamente 500 kg y se incluye en el cálculo de peso bruto.",
  },
  {
    label: "6. Observaciones",
    elementId: "tut-observaciones",
    description:
      "Campo opcional para notas o instrucciones especiales del viaje, como condiciones de entrega o advertencias.",
  },
  {
    label: "7. Buscar Pedidos",
    elementId: "tut-buscar",
    description:
      "Filtra los contratos pendientes por número de pedido, cliente o código de producto para encontrar rápidamente lo que necesitas.",
  },
  {
    label: "8. Seleccionar Pedidos",
    elementId: "tut-pedidos-tabla",
    description:
      "Marca los contratos que irán en este viaje usando los checkboxes. Puedes seleccionar varios contratos de distintos clientes para el mismo camión. El saldo disponible de cada contrato se muestra en la columna \"Saldo\".",
  },
  {
    label: "9. Chapas este viaje",
    elementId: "tut-chapas-viaje",
    description:
      "Define cuántas chapas de ese contrato irán en este viaje. Puede ser menor al saldo total del contrato si se entregará en varios viajes.",
  },
  {
    label: "10. Configurar Paquetes",
    elementId: "tut-config-paquetes",
    description:
      "Abre el configurador para definir los grupos de paquetes. Ejemplo: 2 paquetes de 15 chapas + 1 paquete de 10 chapas. La suma de chapas debe coincidir con \"Chapas este viaje\".",
  },
  {
    label: "11. Crear Orden",
    elementId: "tut-crear-orden",
    description:
      "Una vez completados todos los campos y configurados los paquetes, haz clic aquí para registrar la orden de carga en el sistema.",
  },
  {
    label: "12. Ver Órdenes",
    elementId: "tut-ver-ordenes",
    description:
      "Una vez creada la orden, haz clic aquí para ir al listado completo de órdenes donde podrás ver el estado, cambiar el estado y acceder al detalle de cada una.",
  },
];

const TutorialOverlay = ({ steps, activeStep, onNext, onPrev, onClose }) => {
  const [rect, setRect] = useState(null);
  const PAD = 10;

  useEffect(() => {
    const step = steps[activeStep];
    const el = document.getElementById(step.elementId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => {
      setRect(el.getBoundingClientRect());
    }, 320);
    return () => clearTimeout(timer);
  }, [activeStep, steps]);

  if (!rect) return null;

  const TOOLTIP_W = 320;
  const TOOLTIP_H = 200;
  const belowSpace = window.innerHeight - rect.bottom;
  const ttTop =
    belowSpace >= TOOLTIP_H + 20
      ? rect.bottom + PAD + 10
      : rect.top - TOOLTIP_H - PAD - 10;
  const ttLeft = Math.max(
    10,
    Math.min(rect.left, window.innerWidth - TOOLTIP_W - 10)
  );

  const step = steps[activeStep];

  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 9996, cursor: "pointer" }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed",
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          borderRadius: 8,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.72)",
          border: "2.5px solid #42a5f5",
          zIndex: 9997,
          pointerEvents: "none",
          transition:
            "top 0.35s cubic-bezier(.4,0,.2,1), left 0.35s cubic-bezier(.4,0,.2,1), width 0.35s, height 0.35s",
        }}
      />
      <div
        style={{
          position: "fixed",
          top: ttTop,
          left: ttLeft,
          width: TOOLTIP_W,
          zIndex: 9998,
          background: "#0d1b2a",
          border: "1.5px solid #42a5f5",
          borderRadius: 12,
          padding: "14px 16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          transition:
            "top 0.35s cubic-bezier(.4,0,.2,1), left 0.35s cubic-bezier(.4,0,.2,1)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ color: "#42a5f5", fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>
            Paso {activeStep + 1} / {steps.length}
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#546e7a", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 2px" }}
            title="Salir del tutorial"
          >
            ✕
          </button>
        </div>
        <div style={{ color: "#ffffff", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
          {step.label}
        </div>
        <div style={{ color: "#b0bec5", fontSize: 13, lineHeight: 1.65, marginBottom: 14 }}>
          {step.description}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={onPrev}
            disabled={activeStep === 0}
            style={{
              background: "transparent",
              border: "1px solid #37474f",
              color: activeStep === 0 ? "#37474f" : "#42a5f5",
              borderRadius: 6,
              padding: "5px 12px",
              cursor: activeStep === 0 ? "default" : "pointer",
              fontSize: 13,
              whiteSpace: "nowrap",
            }}
          >
            ← Anterior
          </button>
          {activeStep < steps.length - 1 ? (
            <button
              onClick={onNext}
              style={{
                background: "#42a5f5",
                border: "none",
                color: "#fff",
                borderRadius: 6,
                padding: "5px 14px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Siguiente →
            </button>
          ) : (
            <button
              onClick={onClose}
              style={{
                background: "rgb(206,220,0)",
                border: "none",
                color: "#000",
                borderRadius: 6,
                padding: "5px 14px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              ¡Listo!
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const OrdenCarga = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [pedidos, setPedidos] = useState([]);
  const [selectedVehiculo, setSelectedVehiculo] = useState("");
  const [selectedVehiculoInfo, setSelectedVehiculoInfo] = useState(null);
  const [modalCamionOpen, setModalCamionOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectionModel, setSelectionModel] = useState([]);
  // const [selectedVehiculo, setSelectedVehiculo] = useState("");
  const [motoristaNombre, setMotoristaNombre] = useState("");
  const [taraKg, setTaraKg] = useState(0);
  const [caballeteCantidad, setCaballeteCantidad] = useState(0);
  const [observaciones, setObservaciones] = useState("");
  const [fechaCarga, setFechaCarga] = useState(new Date().toISOString().slice(0, 10));
  const [isLoading, setIsLoading] = useState(false);
  const [detallePaquetesPorPedido, setDetallePaquetesPorPedido] = useState({});
  const [cantidadPorPedido, setCantidadPorPedido] = useState({});
  const [modalPaquetesOpen, setModalPaquetesOpen] = useState(false);
  const [pedidoEnEdicion, setPedidoEnEdicion] = useState(null);
  const [gruposPaquetesDraft, setGruposPaquetesDraft] = useState([]);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  const normalizePedidoRow = (pedido, index) => {
    const idPedido = pedido?.id ?? pedido?.id_pedido ?? pedido?.n_pedido;
    const cantidadTotal = toNumber(pedido?.cantidad ?? pedido?.total);
    const cantidadEntregada = toNumber(pedido?.cantidad_entregada);
    const cantidadRestante = pedido?.cantidad_restante != null
      ? toNumber(pedido.cantidad_restante)
      : cantidadTotal - cantidadEntregada;

    return {
      ...pedido,
      id: idPedido ?? `pendiente-${index}`,
      n_pedido: pedido?.n_pedido ?? idPedido ?? "-",
      cliente: pedido?.cliente ?? pedido?.descripcion_cliente ?? "-",
      cantidad: cantidadTotal,
      cantidad_entregada: cantidadEntregada,
      cantidad_restante: cantidadRestante,
      cod: pedido?.cod ?? pedido?.producto ?? "-",
    };
  };

  const fetchData = async () => {
    try {
      const pedidosPendientes = await getPedidosPendientes();
      setPedidos(
        Array.isArray(pedidosPendientes) ? pedidosPendientes.map(normalizePedidoRow) : []
      );
    } catch (error) {
      toast.error(error.message || "No se pudieron cargar los datos");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredPedidos = useMemo(() => {
    if (!searchValue.trim()) {
      return pedidos;
    }

    const q = searchValue.toLowerCase();
    return pedidos.filter((item) => {
      const pedido = String(item.n_pedido || "").toLowerCase();
      const cliente = String(item.cliente || "").toLowerCase();
      const cod = String(item.cod || "").toLowerCase();
      return pedido.includes(q) || cliente.includes(q) || cod.includes(q);
    });
  }, [pedidos, searchValue]);

  const selectedPedidos = useMemo(
    () => pedidos.filter((pedido) => selectionModel.includes(pedido.id)),
    [pedidos, selectionModel]
  );

  useEffect(() => {
    const nuevoPaquetes = {};
    const nuevaCantidad = {};
    selectedPedidos.forEach((p) => {
      const restante = toNumber(p.cantidad_restante ?? p.cantidad);
      nuevoPaquetes[p.id] = detallePaquetesPorPedido[p.id] || [{ paquetes: 1, chapasPorPaquete: restante > 0 ? restante : 1 }];
      nuevaCantidad[p.id] = cantidadPorPedido[p.id] ?? restante;
    });
    setDetallePaquetesPorPedido(nuevoPaquetes);
    setCantidadPorPedido(nuevaCantidad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionModel]);

  const normalizeGrupos = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((grupo) => ({
        paquetes: Math.max(1, toNumber(grupo?.paquetes)),
        chapasPorPaquete: Math.max(1, toNumber(grupo?.chapasPorPaquete)),
      }))
      .filter((grupo) => grupo.paquetes > 0 && grupo.chapasPorPaquete > 0);
  };

  const getResumenPaquetes = (pedidoId) => {
    const grupos = normalizeGrupos(detallePaquetesPorPedido[pedidoId]);
    const nro_paquetes = grupos.reduce((acc, grupo) => acc + toNumber(grupo.paquetes), 0);
    const nro_chapas = grupos.reduce(
      (acc, grupo) => acc + toNumber(grupo.paquetes) * toNumber(grupo.chapasPorPaquete),
      0
    );
    const chapas_por_paquete = nro_paquetes > 0 ? Number((nro_chapas / nro_paquetes).toFixed(3)) : 0;

    return { grupos, nro_paquetes, nro_chapas, chapas_por_paquete };
  };

  const getTextoResumenPaquetes = (pedidoId) => {
    const grupos = normalizeGrupos(detallePaquetesPorPedido[pedidoId]);
    if (!grupos.length) return "Sin definir";
    return grupos.map((grupo) => `${grupo.paquetes}x${grupo.chapasPorPaquete}`).join(" + ");
  };

  const openModalPaquetes = (pedido) => {
    const gruposActuales = normalizeGrupos(detallePaquetesPorPedido[pedido.id]);
    setPedidoEnEdicion(pedido);
    setGruposPaquetesDraft(
      gruposActuales.length
        ? gruposActuales
        : [{ paquetes: 1, chapasPorPaquete: Math.max(1, toNumber(pedido.cantidad)) }]
    );
    setModalPaquetesOpen(true);
  };

  const closeModalPaquetes = () => {
    setModalPaquetesOpen(false);
    setPedidoEnEdicion(null);
    setGruposPaquetesDraft([]);
  };

  const handleAgregarGrupoDraft = () => {
    setGruposPaquetesDraft((prev) => [...prev, { paquetes: 1, chapasPorPaquete: 1 }]);
  };

  const handleEliminarGrupoDraft = (indexToDelete) => {
    setGruposPaquetesDraft((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, index) => index !== indexToDelete);
    });
  };

  const handleCambiarGrupoDraft = (indexToEdit, field, value) => {
    setGruposPaquetesDraft((prev) =>
      prev.map((grupo, index) => {
        if (index !== indexToEdit) return grupo;
        return { ...grupo, [field]: Math.max(1, toNumber(value)) };
      })
    );
  };

  const handleGuardarGruposDraft = () => {
    if (!pedidoEnEdicion) return;
    const gruposNormalizados = normalizeGrupos(gruposPaquetesDraft);
    if (!gruposNormalizados.length) {
      toast.warning("Debe ingresar al menos un grupo valido de paquetes");
      return;
    }
    setDetallePaquetesPorPedido((prev) => ({
      ...prev,
      [pedidoEnEdicion.id]: gruposNormalizados,
    }));
    closeModalPaquetes();
  };

  const formatFechaEntrega = (value) => {
    if (!value) return "-";
    const rawValue = String(value).trim();
    const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day}/${month}/${year}`;
    }
    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) return rawValue;
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getEspesorFromCod = (cod) => {
    if (!cod) return 0;
    const match = String(cod).match(/(\d{3})(?!.*\d)/);
    if (match) {
      const [a, b, c] = match[1].split("").map(Number);
      if (!isNaN(a) && !isNaN(b)) return a + b;
    }
    return 0;
  };

  const buildItemFromPedido = (pedido) => {
    const areaM2 = toNumber(pedido.area_m2);
    const kgM2 = toNumber(pedido.kg_m2);
    const pesoKg = toNumber(pedido.peso_kg) || areaM2 * kgM2;
    let espesor = toNumber(pedido?.espesor_mm);
    if (!espesor) espesor = getEspesorFromCod(pedido?.cod || pedido?.producto);
    const resumenPaquetes = getResumenPaquetes(pedido.id);
    const nro_paquetes = resumenPaquetes.nro_paquetes || 1;
    const nro_chapas = cantidadPorPedido[pedido.id] ?? resumenPaquetes.nro_chapas ?? toNumber(pedido?.cantidad_restante ?? pedido?.cantidad);
    const chapas_por_paquete = resumenPaquetes.chapas_por_paquete || toNumber(pedido?.chapas_por_paquete);

    return {
      id_producto: pedido?.id_producto ?? null,
      pedido_detalle_id: pedido?.pedido_detalle_id ?? null,
      producto_descripcion: pedido?.cod || pedido?.producto || "SIN DESCRIPCION",
      espesor_mm: espesor,
      nro_paquetes,
      chapas_por_paquete,
      nro_chapas,
      comp_mm: toNumber(pedido?.comp_mm),
      altura_mm: toNumber(pedido?.altura_mm),
      area_m2: areaM2,
      peso_kg: pesoKg,
      kg_m2: kgM2,
    };
  };

  const buildTotales = (items) => {
    const totalPaquetes = items.reduce((acc, item) => acc + toNumber(item.nro_paquetes), 0);
    const totalChapas = items.reduce((acc, item) => acc + toNumber(item.nro_chapas), 0);
    const pesoNeto = items.reduce((acc, item) => acc + toNumber(item.peso_kg), 0);
    const pesoBruto = pesoNeto + toNumber(caballeteCantidad) * 500;
    const pesoTotalConTara = pesoBruto + toNumber(taraKg);

    return {
      total_paquetes: totalPaquetes,
      total_chapas: totalChapas,
      peso_neto_vidrio_kg: Number(pesoNeto.toFixed(3)),
      peso_bruto_cavallete_kg: Number(pesoBruto.toFixed(3)),
      peso_total_con_tara_kg: Number(pesoTotalConTara.toFixed(3)),
    };
  };

  const handleSelectCamion = (camion) => {
    setSelectedVehiculo(camion.id);
    setSelectedVehiculoInfo(camion);
    setModalCamionOpen(false);
  };

  const handleCrearOrdenes = async () => {
    if (!selectedVehiculo) { toast.warning("Seleccione un vehiculo para asignar"); return; }
    if (!motoristaNombre.trim()) { toast.warning("Ingrese el nombre del motorista"); return; }
    if (!selectionModel.length) { toast.warning("Seleccione al menos un pedido pendiente"); return; }
    if (toNumber(caballeteCantidad) <= 0) { toast.warning("La cantidad de caballete es obligatoria y debe ser mayor a cero"); return; }

    const pedidoInconsistente = selectedPedidos.find((pedido) => {
      const resumen = getResumenPaquetes(pedido.id);
      if (!resumen.nro_paquetes || !resumen.nro_chapas) return true;
      const cantidadViaje = toNumber(cantidadPorPedido[pedido.id] ?? pedido.cantidad_restante ?? pedido.cantidad);
      return resumen.nro_chapas !== cantidadViaje;
    });

    if (pedidoInconsistente) {
      toast.warning(`Revise el desglose del pedido ${pedidoInconsistente.n_pedido}: la suma de chapas por paquete debe coincidir con la cantidad asignada a este viaje`);
      return;
    }

    const pedidoSinCantidad = selectedPedidos.find((pedido) => {
      const cant = toNumber(cantidadPorPedido[pedido.id]);
      const restante = toNumber(pedido.cantidad_restante ?? pedido.cantidad);
      return cant <= 0 || cant > restante;
    });

    if (pedidoSinCantidad) {
      toast.warning(`La cantidad del pedido ${pedidoSinCantidad.n_pedido} debe ser mayor a 0 y no superar el saldo restante (${pedidoSinCantidad.cantidad_restante ?? pedidoSinCantidad.cantidad})`);
      return;
    }

    setIsLoading(true);
    try {
      const codEmpresa = Number(secureStorage.getItem("cod_empresa")) || 1;
      const usuario = Number(secureStorage.getItem("id")) || null;
      const vehiculoId = Number(selectedVehiculo);
      const placa = selectedVehiculoInfo?.chapa || "SIN-PLACA";
      const pedidoIds = selectionModel.map((id) => Number(id));
      const cantidadesAsignadas = pedidoIds.map((id) => toNumber(cantidadPorPedido[id]));
      const items = selectedPedidos.map(buildItemFromPedido);
      const totales = buildTotales(items);

      const payload = {
        pedidos: pedidoIds,
        cantidades_asignadas: cantidadesAsignadas,
        fecha_carga: fechaCarga,
        id_cliente: Number(selectedPedidos[0]?.id_cliente || 0),
        id_vehiculo: vehiculoId,
        motorista_nombre: motoristaNombre.trim(),
        status: "AGUARDANDO_CARREGAMENTO",
        tara_kg: toNumber(taraKg),
        caballete_cantidad: toNumber(caballeteCantidad),
        observaciones: observaciones || null,
        creado_por: usuario,
        cod_empresa: codEmpresa,
        placas: [{ tipo: "TRACTOR", placa }],
        items,
        totales,
      };

      await postOrdenCarga(payload);
      toast.success(`Orden de carga creada con ${pedidoIds.length} pedido(s)`);
      setSelectionModel([]);
      setMotoristaNombre("");
      setObservaciones("");
      setTaraKg(0);
      setCaballeteCantidad(0);
      setDetallePaquetesPorPedido({});
      setCantidadPorPedido({});
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Error al crear orden de carga");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTutorialNext = () => {
    const nextStep = tutorialStep + 1;
    // Auto-selecciona el primer pedido disponible al llegar a los pasos del panel de paquetes
    if (nextStep >= 8 && nextStep <= 10 && selectionModel.length === 0 && pedidos.length > 0) {
      setSelectionModel([pedidos[0].id]);
    }
    setTutorialStep(nextStep);
  };

  const pedidosColumns = [
    { field: "id", headerName: "ID", flex: 0.3 },
    { field: "n_pedido", headerName: "Pedido", flex: 0.4 },
    { field: "cliente", headerName: "Cliente", flex: 0.7 },
    { field: "cod", headerName: "Codigo", flex: 1.7, cellClassName: "name-column--cell" },
    { field: "cantidad", headerName: "Total contrato", flex: 0.4 },
    {
      field: "cantidad_en_ordenes",
      headerName: "En órdenes",
      flex: 0.45,
      type: "number",
      align: "center",
      renderCell: (params) => {
        const val = toNumber(params.value);
        return (
          <Typography
            variant="body2"
            sx={{ color: val > 0 ? "#ff9800" : "inherit", fontWeight: val > 0 ? 700 : 400 }}
          >
            {val}
          </Typography>
        );
      },
    },
    { field: "cantidad_entregada", headerName: "Entregado", flex: 0.4 },
    { field: "cantidad_restante", headerName: "Saldo", flex: 0.4 },
    {
      field: "fecha_entrega",
      headerName: "Fecha Entrega",
      flex: 0.9,
      valueGetter: (params) => formatFechaEntrega(params.row.fecha_entrega),
      cellClassName: "name-column--cell",
    },
  ];

  return (
    <Box m="20px">
      {isLoading && <LoadingSpinner />}

      {tutorialOpen && (
        <TutorialOverlay
          steps={TUTORIAL_STEPS}
          activeStep={tutorialStep}
          onNext={handleTutorialNext}
          onPrev={() => setTutorialStep((s) => Math.max(s - 1, 0))}
          onClose={() => setTutorialOpen(false)}
        />
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Header title="Orden de Carga" subtitle="Crear orden con pedidos pendientes y gestionar estado" />
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          onClick={() => { setTutorialStep(0); setTutorialOpen(true); }}
          sx={{
            cursor: "pointer",
            color: "#42a5f5",
            border: "1px solid #42a5f5",
            borderRadius: "20px",
            px: 1.5,
            py: 0.5,
            mt: 1,
            transition: "background 0.2s",
            "&:hover": { background: "rgba(66,165,245,0.12)" },
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
            Tutorial
          </Typography>
        </Box>
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(6, minmax(120px, 1fr))" gap={1} mb={2}>
        <div id="tut-vehiculo">
          <TextField
            size="small"
            label="Vehículo"
            value={
              selectedVehiculoInfo
                ? `${selectedVehiculoInfo.chapa || "Sin chapa"} - ${selectedVehiculoInfo.brand || "Sin marca"}`
                : ""
            }
            placeholder="Seleccionar vehículo..."
            onClick={() => setModalCamionOpen(true)}
            InputProps={{ readOnly: true, style: { cursor: "pointer" } }}
            inputProps={{ style: { cursor: "pointer" } }}
            fullWidth
          />
        </div>

        <div id="tut-motorista">
          <TextField
            size="small"
            label="Motorista"
            value={motoristaNombre}
            onChange={(event) => setMotoristaNombre(event.target.value)}
            fullWidth
          />
        </div>

        <div id="tut-fecha-carga">
          <TextField
            size="small"
            type="date"
            label="Fecha Carga"
            value={fechaCarga}
            onChange={(event) => setFechaCarga(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </div>

        <div id="tut-tara">
          <TextField
            size="small"
            type="number"
            label="Tara Kg"
            value={taraKg}
            onChange={(event) => setTaraKg(event.target.value)}
            fullWidth
          />
        </div>

        <div id="tut-caballete">
          <TextField
            size="small"
            type="number"
            label="Caballete Cant."
            value={caballeteCantidad}
            onChange={(event) => setCaballeteCantidad(event.target.value)}
            fullWidth
          />
        </div>

        <div id="tut-observaciones">
          <TextField
            size="small"
            label="Observaciones"
            value={observaciones}
            onChange={(event) => setObservaciones(event.target.value)}
            fullWidth
          />
        </div>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <div id="tut-buscar">
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            maxWidth="420px"
            width="100%"
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar por pedido, cliente o codigo"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </div>

        <Button
          id="tut-crear-orden"
          variant="contained"
          color="secondary"
          startIcon={<AssignmentTurnedInIcon />}
          onClick={handleCrearOrdenes}
        >
          Crear Orden(es)
        </Button>
      </Box>

      <Box
        id="tut-pedidos-tabla"
        m="10px 0 0 0"
        height="42vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[900], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[900] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
        }}
      >
        <DataGrid
          rows={filteredPedidos}
          columns={pedidosColumns}
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
          disableSelectionOnClick
          pageSize={8}
          rowsPerPageOptions={[8, 20, 50]}
        />
      </Box>

      {selectedPedidos.length > 0 && (
        <Box mt={2} mb={2}>
          <Typography variant="h6" mb={1}>
            Definir chapas por paquete (ejemplo: 15,15,15,10)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(100px, 1fr))",
              gap: 1,
              alignItems: "center",
              background: colors.primary[400],
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography fontWeight="bold">Pedido</Typography>
            <Typography fontWeight="bold">Cliente</Typography>
            <Typography fontWeight="bold">Codigo</Typography>
            <Typography fontWeight="bold">Saldo contrato</Typography>
            <Typography fontWeight="bold">Chapas este viaje</Typography>
            <Typography fontWeight="bold">Definir paquetes</Typography>
            <Typography fontWeight="bold">Paquetes (calc.)</Typography>
            {selectedPedidos.map((pedido, index) => (
              <React.Fragment key={pedido.id}>
                <Typography>{pedido.n_pedido}</Typography>
                <Typography>{pedido.cliente}</Typography>
                <Typography>{pedido.cod}</Typography>
                <Typography>{pedido.cantidad_restante ?? pedido.cantidad}</Typography>
                <div id={index === 0 ? "tut-chapas-viaje" : undefined}>
                  <TextField
                    type="number"
                    size="small"
                    value={cantidadPorPedido[pedido.id] ?? ""}
                    inputProps={{ min: 1, max: pedido.cantidad_restante ?? pedido.cantidad }}
                    onChange={(e) => {
                      const val = Math.max(1, toNumber(e.target.value));
                      const max = toNumber(pedido.cantidad_restante ?? pedido.cantidad);
                      setCantidadPorPedido((prev) => ({ ...prev, [pedido.id]: Math.min(val, max) }));
                    }}
                    sx={{ maxWidth: 100 }}
                  />
                </div>
                <Box
                  id={index === 0 ? "tut-config-paquetes" : undefined}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <IconButton
                    color="secondary"
                    onClick={() => openModalPaquetes(pedido)}
                    title="Configurar paquetes"
                  >
                    <TuneIcon />
                  </IconButton>
                  <Typography variant="body2">{getTextoResumenPaquetes(pedido.id)}</Typography>
                </Box>
                <Typography>{getResumenPaquetes(pedido.id).nro_paquetes}</Typography>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}

      <Dialog open={modalPaquetesOpen} onClose={closeModalPaquetes} fullWidth maxWidth="md">
        <DialogTitle>
          Configurar paquetes - Pedido {pedidoEnEdicion?.n_pedido || "-"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            Ingrese grupos de paquetes. Ejemplo: 2 paquetes de 15 chapas y 1 paquete de 10 chapas.
          </Typography>
          <Box display="grid" gridTemplateColumns="1.2fr 1.2fr auto" gap={1} mb={1}>
            <Typography fontWeight="bold">Cantidad de paquetes</Typography>
            <Typography fontWeight="bold">Chapas por paquete</Typography>
            <Typography fontWeight="bold">Acciones</Typography>
          </Box>
          {gruposPaquetesDraft.map((grupo, index) => (
            <Box key={`grupo-${index}`} display="grid" gridTemplateColumns="1.2fr 1.2fr auto" gap={1} mb={1}>
              <TextField
                type="number"
                size="small"
                value={grupo.paquetes}
                inputProps={{ min: 1 }}
                onChange={(e) => handleCambiarGrupoDraft(index, "paquetes", e.target.value)}
              />
              <TextField
                type="number"
                size="small"
                value={grupo.chapasPorPaquete}
                inputProps={{ min: 1 }}
                onChange={(e) => handleCambiarGrupoDraft(index, "chapasPorPaquete", e.target.value)}
              />
              <IconButton
                color="error"
                onClick={() => handleEliminarGrupoDraft(index)}
                disabled={gruposPaquetesDraft.length <= 1}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
            <Button variant="outlined" color="secondary" startIcon={<AddCircleOutlineIcon />} onClick={handleAgregarGrupoDraft}>
              Agregar otro grupo
            </Button>
            <Typography variant="body2">
              Total paquetes: {normalizeGrupos(gruposPaquetesDraft).reduce((acc, g) => acc + g.paquetes, 0)} | Total chapas: {normalizeGrupos(gruposPaquetesDraft).reduce((acc, g) => acc + g.paquetes * g.chapasPorPaquete, 0)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModalPaquetes}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleGuardarGruposDraft}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          id="tut-ver-ordenes"
          variant="outlined"
          color="secondary"
          startIcon={<AutorenewIcon />}
          onClick={() => navigate("/view/ordenCargaListado")}
        >
          Ver Órdenes Creadas
        </Button>
      </Box>

      <ModalCamion
        open={modalCamionOpen}
        onClose={() => setModalCamionOpen(false)}
        onSelectVehiculos={handleSelectCamion}
      />
    </Box>
  );
};

export default OrdenCarga;
